import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const PRIMARY_COLOR_VARIABLES = {
  iris: {
    '--blue-3': '240 241 254',
    '--blue-4': '230 231 255',
    '--blue-5': '218 220 255',
    '--blue-8': '155 158 240',
    '--blue-9': '91 91 214',
    '--blue-10': '81 81 205',
    '--blue-11': '87 83 198',
    '--solid-blue': '230 231 255',
    '--border-blue': '91, 91, 214, 0.5',
  },
  blue: {
    '--blue-3': '233 243 255',
    '--blue-4': '218 236 255',
    '--blue-5': '201 226 255',
    '--blue-8': '117 171 247',
    '--blue-9': '39 129 246',
    '--blue-10': '16 115 233',
    '--blue-11': '8 109 224',
    '--solid-blue': '218 236 255',
    '--border-blue': '39, 129, 246, 0.5',
  },
  green: {
    '--blue-3': '224 248 243',
    '--blue-4': '204 243 234',
    '--blue-5': '184 234 224',
    '--blue-8': '83 185 171',
    '--blue-9': '18 165 148',
    '--blue-10': '13 155 138',
    '--blue-11': '0 133 115',
    '--solid-blue': '204 243 234',
    '--border-blue': '18, 165, 148, 0.5',
  },
  amber: {
    '--blue-3': '255 247 194',
    '--blue-4': '255 238 156',
    '--blue-5': '251 229 119',
    '--blue-8': '226 163 54',
    '--blue-9': '255 163 26',
    '--blue-10': '232 132 0',
    '--blue-11': '171 100 0',
    '--solid-blue': '255 238 156',
    '--border-blue': '255, 163, 26, 0.5',
  },
  violet: {
    '--blue-3': '244 240 254',
    '--blue-4': '235 228 255',
    '--blue-5': '225 217 255',
    '--blue-8': '169 153 236',
    '--blue-9': '110 86 207',
    '--blue-10': '100 84 196',
    '--blue-11': '101 85 183',
    '--solid-blue': '235 228 255',
    '--border-blue': '110, 86, 207, 0.5',
  },
  ruby: {
    '--blue-3': '254 234 237',
    '--blue-4': '255 220 225',
    '--blue-5': '255 206 214',
    '--blue-8': '229 146 163',
    '--blue-9': '255 38 91',
    '--blue-10': '229 70 102',
    '--blue-11': '202 36 77',
    '--solid-blue': '255 220 225',
    '--border-blue': '255, 38, 91, 0.5',
  },
};

const DARK_SCHEME_VARIABLES = {
  mint: {
    '--background-color': '13 26 29',
    '--surface-1': '14 29 33',
    '--surface-2': '17 38 43',
    '--solid-1': '14 29 33',
    '--solid-2': '17 38 43',
    '--solid-3': '23 51 58',
    '--alpha-1': '18, 59, 66, 0.55',
    '--alpha-3': '18, 45, 51, 0.92',
  },
  navy: {
    '--background-color': '23 34 54',
    '--surface-1': '23 34 54',
    '--surface-2': '31 45 70',
    '--solid-1': '23 34 54',
    '--solid-2': '31 45 70',
    '--solid-3': '42 58 88',
    '--alpha-1': '57, 75, 115, 0.52',
    '--alpha-3': '36, 50, 77, 0.94',
  },
  mirage: {
    '--background-color': '12 20 34',
    '--surface-1': '13 21 36',
    '--surface-2': '18 29 48',
    '--solid-1': '13 21 36',
    '--solid-2': '18 29 48',
    '--solid-3': '25 40 66',
    '--alpha-1': '31, 52, 85, 0.55',
    '--alpha-3': '18, 29, 48, 0.94',
  },
  cinder: {
    '--background-color': '18 18 20',
    '--surface-1': '20 20 23',
    '--surface-2': '28 28 32',
    '--solid-1': '20 20 23',
    '--solid-2': '28 28 32',
    '--solid-3': '43 43 49',
    '--alpha-1': '48, 48, 55, 0.55',
    '--alpha-3': '30, 30, 35, 0.94',
  },
  black: {
    '--background-color': '6 6 7',
    '--surface-1': '8 8 9',
    '--surface-2': '13 13 15',
    '--solid-1': '8 8 9',
    '--solid-2': '13 13 15',
    '--solid-3': '26 26 30',
    '--alpha-1': '34, 34, 39, 0.55',
    '--alpha-3': '14, 14, 16, 0.96',
  },
};

const DARK_DOODLE_PATTERN = `url("/dashboard/images/bg-chat-tile-dark.png")`;
const CLASSIC_CROSS_PATTERN = `url("/dashboard/images/bg-chat-tile-light.png")`;

export const WALLPAPER_OPTIONS = {
  none: { bg: null, pattern: null },
  whatsapp: { bg: '#dce9d3', pattern: null },
  beige: { bg: '#ece5dd', pattern: null },
  sky: { bg: '#d5e8f5', pattern: null },
  lavender: { bg: '#e4d8f5', pattern: null },
  rose: { bg: '#f5d8e8', pattern: null },
  dark_pattern: { bg: '#111b21', pattern: DARK_DOODLE_PATTERN },
  classic: { bg: '#efeae2', pattern: CLASSIC_CROSS_PATTERN },
};

// Backward-compat alias
export const WALLPAPER_COLORS = Object.fromEntries(
  Object.entries(WALLPAPER_OPTIONS)
    .filter(([, v]) => v.bg)
    .map(([k, v]) => [k, v.bg])
);

const clearVariables = variableGroups => {
  variableGroups.forEach(group => {
    Object.keys(group).forEach(variable => {
      document.body.style.removeProperty(variable);
    });
  });
};

const applyVariables = variables => {
  Object.entries(variables || {}).forEach(([variable, value]) => {
    document.body.style.setProperty(variable, value);
  });
};

export const setPrimaryColor = color => {
  clearVariables(Object.values(PRIMARY_COLOR_VARIABLES));
  applyVariables(
    PRIMARY_COLOR_VARIABLES[color] || PRIMARY_COLOR_VARIABLES.blue
  );
};

export const setDarkColorScheme = isDarkMode => {
  clearVariables(Object.values(DARK_SCHEME_VARIABLES));
  if (!isDarkMode) return;

  const selectedDarkScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.DARK_COLOR_SCHEME) || 'default';
  if (selectedDarkScheme === 'default') return;

  applyVariables(DARK_SCHEME_VARIABLES[selectedDarkScheme]);
};

export const setConversationWallpaper = () => {
  const key =
    LocalStorage.get(LOCAL_STORAGE_KEYS.CONVERSATION_WALLPAPER) || 'none';
  const option = WALLPAPER_OPTIONS[key];
  if (option?.bg) {
    document.body.style.setProperty('--conversation-bg', option.bg);
    document.body.style.setProperty(
      '--conversation-pattern',
      option.pattern || 'none'
    );
  } else {
    document.body.style.removeProperty('--conversation-bg');
    document.body.style.removeProperty('--conversation-pattern');
  }
};

export const setColorTheme = isOSOnDarkMode => {
  const selectedColorScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto';
  const isDarkMode =
    (selectedColorScheme === 'auto' && isOSOnDarkMode) ||
    selectedColorScheme === 'dark';
  if (isDarkMode) {
    document.body.classList.add('dark');
    document.documentElement.style.setProperty('color-scheme', 'dark');
  } else {
    document.body.classList.remove('dark');
    document.documentElement.style.setProperty('color-scheme', 'light');
  }
  setDarkColorScheme(isDarkMode);
  setPrimaryColor(LocalStorage.get(LOCAL_STORAGE_KEYS.PRIMARY_COLOR) || 'blue');
  setConversationWallpaper();
};
